/** The report's Markdown as the reader shows it (item 71): the document the
 *  PDF / Word / Markdown downloads are made of stays as written; the reader
 *  reads it in sections - the summary, then each "## " section by the key the
 *  server names for it (report_writer.section_keys, in order) - and draws its
 *  tables itself. Which columns are figures is the document's own: the server
 *  marks them end-aligned in the separator row (`---:`), as Word and the PDF
 *  read it too. Pure: no React, unit-tested (report_body.test.mjs). */

/** The isolates the server puts round a figure in a Hebrew report (report_format.isolate). */
const ISOLATES = /[⁦⁧⁨⁩‎‏]/g;

export type Tone = 'up' | 'down' | null;

/** Whether a change is good news: more conversations is, more cost is not; an answer's share is neither. */
export type Verdict = 'good' | 'bad' | null;

export interface Cell {
  text: string;
  /** Written in italics by the server: the "Other (n)" row's label. */
  emphasis: boolean;
  /** A change in a figure column: which way it points ("+42 (+13%)" up, "−636 (−95.2%)" down). */
  tone: Tone;
  verdict: Verdict;
}

export type Block =
  | { kind: 'text'; text: string }
  | { kind: 'table'; header: string[]; figures: boolean[]; rows: Cell[][] };

export interface Section {
  /** The section's key ('trend', 'organizations'…), or null for one no key names. */
  key: string | null;
  heading: string;
  blocks: Block[];
}

export interface ParsedReport {
  /** The line under the title: the days, what they are compared with, the scope. */
  subtitle: string;
  /** What comes before the first section, without the title and that line. */
  lead: string;
  sections: Section[];
}

/** The report in sections, the n-th "## " under the n-th of `keys`. */
export function parseReport(markdown: string, keys: (string | null)[] = []): ParsedReport {
  const parts = (markdown || '').replace(/\r\n/g, '\n').split(/\n(?=## )/);
  const head = (parts[0].startsWith('## ') ? '' : parts.shift() || '').split('\n');
  // The server writes "# title", then the italic line, then the summary: only that line is the subtitle.
  const titleAt = head.findIndex((line) => /^# /.test(line));
  const nextAt = head.findIndex((line, i) => i > titleAt && line.trim() !== '');
  const italic = nextAt >= 0 && /^\*[^*].*\*$/.test(head[nextAt].trim());
  const subtitle = italic ? head[nextAt].trim().slice(1, -1) : '';
  const lead = head.filter((_, i) => i !== titleAt && !(italic && i === nextAt)).join('\n').trim();
  const sections = parts.map((part, i) => {
    const [first, ...rest] = part.split('\n');
    const key = keys[i] ?? null;
    return { key, heading: first.replace(/^##\s+/, '').trim(), blocks: blocks(rest.join('\n'), key) };
  });
  return { subtitle, lead, sections };
}

const SEPARATOR = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/;

function split(line: string): string[] {
  const inner = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return inner.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, '|').trim());
}

function tone(text: string): Tone {
  // The server writes a change's sign first; a figure column's other cells carry none.
  if (/^\+\s*[\d$]/.test(text)) return 'up';
  if (/^[−-]\s*[\d$]/.test(text)) return 'down';
  return null;
}

/** An amount of money changes ("+$0.07"): less is good. An answer's share (templates) is no one's news. */
function verdict(text: string, direction: Tone, section: string | null): Verdict {
  if (!direction || section === 'templates') return null;
  const worse = /^[+−-]\s*\$/.test(text) ? 'up' : 'down';
  return direction === worse ? 'bad' : 'good';
}

function cell(text: string, figure: boolean, section: string | null): Cell {
  const em = /^\*([^*].*)\*$/.exec(text);
  const bare = text.replace(ISOLATES, '').trim();
  const direction = figure ? tone(bare) : null;
  return { text: em ? em[1] : text, emphasis: !!em, tone: direction, verdict: verdict(bare, direction, section) };
}

/** A section's body as text and the tables in it (the server's pipe tables). */
export function blocks(body: string, section: string | null = null): Block[] {
  const out: Block[] = [];
  let text: string[] = [];
  let table: string[] = [];
  const flushText = () => {
    if (text.join('\n').trim()) out.push({ kind: 'text', text: text.join('\n').trim() });
    text = [];
  };
  const flushTable = () => {
    if (table.length >= 2 && SEPARATOR.test(table[1].trim())) {
      const header = split(table[0]);
      const marks = split(table[1]);
      const figures = header.map((_, i) => /-:$/.test(marks[i] || ''));
      out.push({ kind: 'table', header, figures,
                 rows: table.slice(2).map((line) => split(line).map((c, i) => cell(c, figures[i], section))) });
    } else if (table.length) {
      text.push(...table);
    }
    table = [];
  };
  for (const line of body.split('\n')) {
    if (line.trim().startsWith('|')) {
      if (!table.length) flushText();
      table.push(line);
    } else {
      if (table.length) flushTable();
      text.push(line);
    }
  }
  flushTable();
  flushText();
  return out;
}
